import {PlusOutlined} from '@ant-design/icons'
import {Button, Form, Input, List, Modal, Select, Spin} from 'antd'
import {
  exam_createExaminationInfoAnswer_Api,
  exam_deleteExaminationInfoAnswer_Api,
  exam_editExaminationInfoAnswer_Api,
  exam_getExaminationInfos_Api,
  exam_sortExaminationInfoAnswers_Api,
} from 'apis'
import classNames from 'classnames'
import FieldsFactory, {FieldHeader} from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import UseState from 'components/UseState'
import {Formik, FormikConsumer} from 'formik'
import filterOption from 'helpers/filterOption'
import getTitle from 'helpers/getTitle'
import _, {debounce} from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {FcNumericalSorting12} from 'react-icons/fc'
import AntdConfigWrapper from 'views/AppLocals/custom/AntdConfigWrapper'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault, renderIf, shallowDiff} from 'views/Shared'
import {examSortBy, QuestionTypes} from '../ExaminationPaperContext'
import DnDSorter from './DnDSorter'
import {BiSolidEdit, BiTrash} from "react-icons/bi";

const renderLabel = (item) => (
  <Translate>
    {(t) => (
      <List.Item.Meta
        title={getTitle(item)}
        description={item?.answer_type ? t(item.answer_type) : undefined}
      />
    )}
  </Translate>
)

const InfoAnswerGroupBySelect = ({
  errors,
  values,
  examination_id,
  onChange = NullAsDefault,
  setValues = NullAsDefault,
  debounceTimeout = 500,
  getInfoOptions = (options) => options,
}) => {
  const t = useTranslate()

  const [options, setOptions] = useState([])

  const [answers, set_answers] = useState([])

  const answer_types = [
    QuestionTypes.SINGLE.value,
    QuestionTypes.MULTIPLE.value,
  ].join(',')

  const { isLoading = false, handleAsyncAction = NullAsDefault } = useAsyncAction({
    apiInfo: exam_getExaminationInfos_Api,
    query: bindQueryParam({ id: examination_id }),
    onError: notifyOnError(t),
    onSuccess: (result, data) => {
      const items = Array.from(getResponseItems(data, 'response.data.data'))
      const { examination_info_answers } =
        _.find(items, { id: values?.group_examination_info_id }) || {}
      set_answers(Array.from(examination_info_answers || []))
      setOptions(getInfoOptions(items))
    },
  })

  useEffect(() => {
    handleAsyncAction({
      answer_types,
    })
  }, [values.group_examination_info_id])

  const debounceFetcher = useMemo(() => {
    const loadOptions = (keyword) => {
      setOptions([])
      handleAsyncAction({
        keyword,
        answer_types,
      })
    }
    return debounce(loadOptions, debounceTimeout)
  }, [handleAsyncAction, debounceTimeout])

  return (
    <React.Fragment>
      <SelectWidget
        showSearch={true}
        allowClear={true}
        filterOption={false}
        loading={!!isLoading}
        optionLabelProp="label"
        onSearch={debounceFetcher}
        placeholder={t('group by')}
        onChange={(value) => {
          const { examination_info_answers } =
            _.find(options, { id: value }) || {}
          setValues({
            group_examination_info_id: value || null,
            group_examination_info_answer_id: null,
          })
          set_answers(Array.from(examination_info_answers || []))
        }}
        value={
          !!isLoading ? undefined : _.get(values, 'group_examination_info_id')
        }
        notFoundContent={isLoading ? <Spin size="small" /> : null}>
        {options.map((item) => {
          const { id: value } = item
          return (
            <Select.Option
              key={value}
              value={value}
              label={getTitle(item)}>
              {renderLabel(item)}
            </Select.Option>
          )
        })}
      </SelectWidget>
      <AntdConfigWrapper>
        <SelectWidget
          showSearch={true}
          allowClear={true}
          loading={!!isLoading}
          placeholder={t('answer')}
          filterOption={filterOption}
          disabled={_.isEmpty(answers)}
          onChange={(value) => {
            onChange(createValue('group_examination_info_answer_id', value))
          }}
          value={
            !!isLoading
              ? undefined
              : _.get(values, 'group_examination_info_answer_id')
          }
          status={
            errors?.group_examination_info_answer_id ? 'error' : undefined
          }>
          {answers.map((item) => {
            const { id: value } = item
            return (
              <Select.Option
                key={value}
                value={value}
                label={getTitle(item)}>
                {getTitle(item)}
              </Select.Option>
            )
          })}
        </SelectWidget>
      </AntdConfigWrapper>
    </React.Fragment>
  )
}

const InfoAnswerRow = ({
  type,
  value,
  index,
  onUpdate,
  onRemove,
  order_data,
  move = NullAsDefault,
  examination_id,
  onChange = NullAsDefault,
  canRemove = true,
  editable = false,
  examination_info_id,
  ...props
}) => {
  const t = useTranslate()

  const [isToggle, toggle] = useState()

  const { id, title } = value || {}

  const setValues = (values) => {
    onChange({
      ...value,
      ...values,
    })
  }

  const disabled_title = type === QuestionTypes.BOOL.value

  const is_text_question = type === QuestionTypes.TEXT.value

  const initialValues = useMemo(() => {
    const { group_examination_info, group_examination_info_answer } =
      value || {}
    return {
      ...value,
      group_examination_info_id: group_examination_info?.id,
      group_examination_info_answer_id: group_examination_info_answer?.id,
    }
  }, [value])

  const validate = (values) => {
    const {
      title,
      group_examination_info_id,
      group_examination_info_answer_id,
    } = values
    let errors = {}

    if (_.isEmpty(title)) {
      errors.title = t('required field')
    }

    if (group_examination_info_id && !group_examination_info_answer_id) {
      errors.group_examination_info_answer_id = t('required field')
    }

    return errors
  }

  return (
    <div className="flex items-center gap-2">
      {editable ? (
        <React.Fragment>
          <div
            onClick={() => {
              toggle(true)
            }}
            className="w-full px-2 py-1 text-sm rounded-md background border">
            {value.title || (
              <span className="font-light text-color-500 italic">
                {t('content answer')}
              </span>
            )}
          </div>
          <BiSolidEdit
            size={16}
            onClick={() => {
              toggle(true)
            }}
            className="cursor-pointer text-color-300 hover:text-primary"
          />
          {isToggle && (
            <Modal
              width={600}
              footer={null}
              centered={true}
              open={isToggle}
              destroyOnHidden={true}
              onCancel={() => {
                toggle(false)
              }}>
              <div className="uppercase text-center text-lg text-color-000 font-bold">
                {t('edit answer')}
              </div>
              <Formik
                validate={validate}
                enableReinitialize={true}
                initialValues={initialValues}
                onSubmit={(values) => {
                  const {
                    group_examination_info,
                    group_examination_info_answer,
                    ...rest
                  } = values
                  const changed = shallowDiff(rest, initialValues)
                  onUpdate(changed, { id })
                }}>
                <div className="flex flex-col gap-3 mt-3">
                  <FieldsFactory
                    formSchema={[
                      {
                        title: 'content answer',
                        children: [
                          {
                            name: 'title',
                            component: createControlledFormField({
                              rows: 3,
                              placeholder: 'title',
                              disabled: disabled_title,
                              Component: Input.TextArea,
                            }),
                          },
                        ],
                      },
                      {
                        title: 'description',
                        children: [
                          {
                            name: 'description',
                            component: createControlledFormField({
                              rows: 3,
                              placeholder: 'description',
                              Component: Input.TextArea,
                            }),
                          },
                        ],
                      },
                    ]}
                  />
                  {!is_text_question && (
                    <FieldsFactory
                      formSchema={[
                        {
                          title: 'order',
                          children: [
                            {
                              name: 'order_sort',
                              component: createControlledFormField({
                                type: 'number',
                                placeholder: 'order',
                              }),
                            },
                          ],
                        },
                      ]}
                    />
                  )}
                  <FormikConsumer>
                    {({ values, errors, setValues, handleChange }) => (
                      <InfoAnswerGroupBySelect
                        values={values}
                        errors={errors}
                        onChange={handleChange}
                        examination_id={examination_id}
                        setValues={(object) => {
                          setValues({
                            ...values,
                            ...object,
                          })
                        }}
                        getInfoOptions={(options) =>
                          Array.from(options || []).filter(
                            ({ id }) => id !== examination_info_id
                          )
                        }
                      />
                    )}
                  </FormikConsumer>
                  <FormActionBar submitText="save" />
                </div>
              </Formik>
            </Modal>
          )}
        </React.Fragment>
      ) : is_text_question ? (
        <Input
          name="title"
          value={title}
          disabled={disabled_title}
          onChange={(event) => {
            setValues({
              [event.target.name]: event.target.value,
            })
          }}
        />
      ) : (
        <React.Fragment>
          <Input
            name="title"
            value={t(title)}
            disabled={disabled_title}
            placeholder={t('content answer')}
            onChange={(event) => {
              setValues({
                [event.target.name]: event.target.value,
              })
            }}
          />
        </React.Fragment>
      )}
      {!is_text_question && (
        <BiTrash
            size={16}
            onClick={() => {
              if (canRemove) {
                onRemove(id)
              }
            }}
            className={classNames(
                canRemove
                    ? 'text-red-300 hover:text-red-600 cursor-pointer'
                    : 'cursor-not-allow text-color-500'
            )}
        />
      )}
    </div>
  )
}

export const ExaminationInfoAnswers = ({
  name,
  value,
  onChange,
  onCreate,
  onDelete,
  onUpdate,
  sortAnswers,
  answer_type,
  refreshToken,
  examination_id,
  editable = false,
  examination_info_id,
}) => {
  const t = useTranslate()

  const [form] = Form.useForm()

  const array_values = _.isArray(value) ? examSortBy(value) : []

  useEffect(() => {
    form.setFieldsValue({
      [name]: examSortBy(value),
    })
  }, [value, answer_type])

  const getRemoveOption = useCallback(
    (size) => {
      switch (answer_type) {
        case QuestionTypes.BOOL.value:
        case QuestionTypes.MULTIPLE.value:
          return size > 2
        case QuestionTypes.SINGLE.value:
          return size > 1
        default:
          break
      }
      return size > 1
    },
    [answer_type]
  )

  const isDisabled = useCallback(
    (size) => {
      switch (answer_type) {
        case QuestionTypes.BOOL.value:
          return size >= 2
        case QuestionTypes.MULTIPLE.value:
        case QuestionTypes.SINGLE.value:
        default:
          break
      }
      return false
    },
    [answer_type]
  )

  const is_text_question = answer_type === QuestionTypes.TEXT.value

  return (
    <div className="flex flex-col mb-2">
      <FieldHeader
        title={is_text_question ? 'answer' : 'answers'}
        extra={[
          <span className="text-xs italic text-color-400">
            {`(${t('total')}: ${array_values.length})`}
          </span>,
        ]}
      />
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changedValues, values) => {
          const value = _.get(values, name)
          onChange(createValue(name, value))
        }}>
        <Form.List
          name={name}
          initialValue={array_values}>
          {(fields, { add, remove, move }, { errors }) => {
            const disabled = isDisabled(fields.length)
            const canRemove = getRemoveOption(fields.length)
            return (
              <div className="flex flex-col gap-2">
                {!is_text_question && (
                  <UseState>
                    {([title, setTitle]) => (
                      <Input.Group
                        compact
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}>
                        <Input
                          name="title"
                          value={title}
                          disabled={disabled}
                          addonBefore={
                            Boolean(
                              !editable || _.isEmpty(array_values)
                            ) ? undefined : (
                              <DnDSorter
                                data={array_values}
                                className="flex flex-center cursor-pointer"
                                onSubmit={(array) => {
                                  sortAnswers({
                                    examination_info_answer_ids:
                                      Array.from(array).join(','),
                                  })
                                }}>
                                {({ toggle }) => (
                                  <FcNumericalSorting12
                                    size={20}
                                    onClick={toggle}
                                  />
                                )}
                              </DnDSorter>
                            )
                          }
                          placeholder={t('type the answer and click')}
                          onChange={(event) => {
                            setTitle(event.target.value)
                          }}
                        />
                        <Button
                          type="primary"
                          disabled={_.isEmpty(title) || disabled}
                          icon={<PlusOutlined />}
                          onClick={() => {
                            const _values = {
                              title,
                              order_sort:
                                _.defaultTo(
                                  (_.last(array_values) || { order_sort: 0 })
                                    ?.order_sort,
                                  0
                                ) + 1,
                              id: _.uniqueId('__id_'),
                            }
                            if (_.isFunction(onCreate)) {
                              onCreate(_values)
                            } else {
                              add(_values)
                            }
                            setTitle('')
                          }}
                          className="no-shadow no-text-shadow text-sm font-medium">
                          {t('add')}
                        </Button>
                      </Input.Group>
                    )}
                  </UseState>
                )}
                {fields.length > 0 && (
                  <React.Fragment>
                    <div
                      style={fields.length > 4 ? { height: 160 } : undefined}
                      className={classNames(
                        'flex flex-col space-y-2',
                        !is_text_question &&
                          'border rounded-md p-2 overflow-auto scrollBarThin'
                      )}>
                      {fields.map((field, index) => (
                        <Form.Item
                          {...field}
                          noStyle={true}
                          key={field.key}>
                          <InfoAnswerRow
                            move={move}
                            index={index}
                            type={answer_type}
                            editable={editable}
                            onUpdate={onUpdate}
                            canRemove={canRemove}
                            order_data={array_values}
                            examination_id={examination_id}
                            examination_info_id={examination_info_id}
                            onRemove={
                              canRemove
                                ? (id) => {
                                    if (_.isFunction(onDelete)) {
                                      onDelete({}, { id })
                                    } else {
                                      remove(field.name)
                                    }
                                  }
                                : NullAsDefault
                            }
                          />
                        </Form.Item>
                      ))}
                      <FormikConsumer>
                        {({ errors }) =>
                          renderIf(
                            _.get(errors, 'answers_error_container'),
                            <FieldsFactory
                              formSchema={[
                                {
                                  children: [
                                    {
                                      name: 'answers_error_container',
                                      component: NullAsDefault,
                                    },
                                  ],
                                },
                              ]}
                            />
                          )
                        }
                      </FormikConsumer>
                    </div>
                  </React.Fragment>
                )}
              </div>
            )
          }}
        </Form.List>
      </Form>
    </div>
  )
}

export const UseExaminationInfoAnswers = ({
  children,
  examination_info_id,
  refresh = ({ history }) => {},
  ...props
}) => {
  const t = useTranslate()

  const history = useHistory()

  const [refreshToken, setRefreshToken] = useState()

  const onError = notifyOnError(t)

  const reload = () => {
    setRefreshToken(Date.now())
    refresh({ history })
  }

  const { handleAsyncAction: onCreate } = useAsyncAction({
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.CREATE, t)
      reload()
    },
    query: bindQueryParam({ id: examination_info_id }),
    apiInfo: exam_createExaminationInfoAnswer_Api,
    ...props,
  })

  const { handleAsyncAction: onUpdate } = useAsyncAction({
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.UPDATE, t)
      reload()
    },
    apiInfo: exam_editExaminationInfoAnswer_Api,
    ...props,
  })

  const { handleAsyncAction: onDelete } = useAsyncAction({
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.DELETE, t)
      reload()
    },
    apiInfo: exam_deleteExaminationInfoAnswer_Api,
    ...props,
  })

  const { handleAsyncAction: sortAnswers } = useAsyncAction({
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.DELETE, t)
      reload()
    },
    apiInfo: exam_sortExaminationInfoAnswers_Api,
    query: bindQueryParam({ id: examination_info_id }),
    ...props,
  })

  return _.isFunction(children)
    ? children({
        onCreate,
        onUpdate,
        sortAnswers,
        onDelete: (values, query) => {
          showDeleteConfirmDialog({
            translate: t,
            title: 'delete answer',
            message: 'are you sure you want to delete it answer',
            onOk: () => {
              onDelete(values, query)
            },
          })
        },
        refreshToken,
      })
    : children || null
}
